import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DirectionsCar as VehicleIcon,
  ViewModule as GridViewIcon,
  ViewList as TableViewIcon,
  Sensors as SensorIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import AIRecommendationsPanel from '../components/ai/AIRecommendationsPanel';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import LoadingPage from '../components/common/LoadingPage';
import { formatDateTime } from '../utils/formatters';
import { VEHICLE_TYPES } from '../utils/constants';
import VehicleDetail from '../components/vehicles/VehicleDetail';
import AdvancedDataTable from '../components/common/AdvancedDataTable';

// Componente compacto para el esquemático - OPTIMIZADO PARA MÓVIL
const SimpleSchematic = ({ vehicle, t }) => {
  const theme = useTheme();

  if (!vehicle || !vehicle.positions_count) return null;

  const createLayout = () => {
    if (!vehicle.figure) {
      return [Array.from({ length: vehicle.positions_count }, (_, i) => i + 1)];
    }

    const axleDistribution = vehicle.figure.split(',').map(a => parseInt(a, 10)).filter(num => !isNaN(num));
    const figureTotal = axleDistribution.reduce((sum, positions) => sum + positions, 0);

    let layout = [];
    let positionCounter = 1;

    if (figureTotal === vehicle.positions_count) {
      axleDistribution.forEach(positionsPerAxle => {
        const row = [];
        for (let i = 0; i < positionsPerAxle; i++) {
          row.push(positionCounter++);
        }
        layout.push(row);
      });
    } else {
      const scaleFactor = vehicle.positions_count / figureTotal;
      let remainingPositions = vehicle.positions_count;

      for (let axleIndex = 0; axleIndex < axleDistribution.length; axleIndex++) {
        if (axleIndex === axleDistribution.length - 1) {
          const row = [];
          for (let i = 0; i < remainingPositions; i++) {
            row.push(positionCounter++);
          }
          if (row.length > 0) layout.push(row);
          break;
        }

        const originalPositions = axleDistribution[axleIndex];
        const scaledPositions = Math.round(originalPositions * scaleFactor);
        const positionsToUse = Math.min(scaledPositions, remainingPositions);

        const row = [];
        for (let i = 0; i < positionsToUse; i++) {
          row.push(positionCounter++);
        }

        if (row.length > 0) {
          layout.push(row);
          remainingPositions -= row.length;
        }

        if (remainingPositions <= 0) break;
      }
    }

    return layout;
  };

  const layout = createLayout();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
      {layout.map((row, rowIndex) => (
        <Box
          key={`row-${rowIndex}`}
          sx={{
            display: 'flex',
            gap: 0.5,
            justifyContent: row.length === 2 ? 'space-between' : 'center',
            minWidth: row.length === 2 ? '60px' : 'auto'
          }}
        >
          {row.map(position => {
            const hasSensor = vehicle.vehicle_sensors?.some(vs => vs.position === position);
            const assignedSensor = vehicle.vehicle_sensors?.find(vs => vs.position === position);

            return (
              <Tooltip
                key={`pos-${position}`}
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      pos {position}
                    </Typography>
                    <Typography variant="caption">
                      {hasSensor ?
                        `${assignedSensor?.sensor_type || 'Sensor'} asignado` :
                        t('hardcoded.pages_tpmsactionsummarypage.sin_sensor_asignado')
                      }
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    border: '2px solid',
                    borderColor: hasSensor ? 'success.main' : 'grey.400',
                    borderRadius: 0.5,
                    bgcolor: hasSensor ? 'success.light' : 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: hasSensor ? 'success.dark' : 'grey.600',
                    cursor: 'default',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    '&:hover': {
                      transform: 'scale(1.15)',
                      boxShadow: theme.shadows[2],
                      borderColor: hasSensor ? 'success.dark' : 'primary.main',
                      zIndex: 10
                    }
                  }}
                >
                  {position}
                  {hasSensor && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -3,
                        right: -3,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        border: '1.5px solid white',
                        boxShadow: theme.shadows[1]
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

const VehiclesPage = () => {
  const theme = useTheme();
  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [vehicleDialog, setVehicleDialog] = useState({ open: false, vehicle: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vehicle: null });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [vehicleScopeFilter, setVehicleScopeFilter] = useState('all');

  const { t } = useTranslation();

  // API calls
  const {
    data: vehicles,
    loading: vehiclesLoading,
    refetch: refetchVehicles
  } = useApiCall(api.vehicles.getAll);

  const {
    data: vehicleConfigs,
    loading: configsLoading
  } = useApiCall(api.vehicles.getConfigurations);

  const {
    data: sensors,
    loading: sensorsLoading
  } = useApiCall(api.sensors.getAll);

  // Mutations
  const { mutate: createVehicle, loading: creating } = useMutation(api.vehicles.create, {
    onSuccess: () => {
      showSuccess(t('vehicles.actions.vehicleCreated'));
      setVehicleDialog({ open: false, vehicle: null });
      refetchVehicles();
    },
    onError: (error) => showError(error.message)
  });

  const { mutate: deleteVehicle, loading: deleting } = useMutation(api.vehicles.delete, {
    onSuccess: () => {
      showSuccess(t('vehicles.actions.vehicleDeleted'));
      setDeleteDialog({ open: false, vehicle: null });
      refetchVehicles();
    },
    onError: (error) => showError(error.message)
  });

  // Flatten child vehicles from API response
  const flattenVehicles = (vehiclesFromAPI) => {
    if (!vehiclesFromAPI) return [];

    const allVehicles = [];

    vehiclesFromAPI.forEach(vehicle => {
      allVehicles.push(vehicle);

      if (vehicle.child_vehicles && vehicle.child_vehicles.length > 0) {
        vehicle.child_vehicles.forEach(child => {
          allVehicles.push(child);
        });
      }
    });

    return allVehicles;
  };

  const allVehicles = flattenVehicles(vehicles);

  const vehicleSchema = Yup.object({
    alias: Yup.string().max(100, t('hardcoded.components_vehicles_vehicledetail.el_alias_debe_tener_menos')),
    make: Yup.string().max(50, t('hardcoded.components_vehicles_vehicledetail.la_marca_debe_tener_menos')),
    model: Yup.string().max(50, t('hardcoded.components_vehicles_vehicledetail.el_modelo_debe_tener_menos')),
    type: Yup.string(),
    year: Yup.number()
      .min(1900, t('hardcoded.components_vehicles_vehicledetail.el_ano_debe_ser_posterior'))
      .max(new Date().getFullYear() + 1, t('hardcoded.components_vehicles_vehicledetail.el_ano_no_puede_ser')),
    configuration_id: Yup.number().required(t('hardcoded.components_vehicles_vehicledetail.la_configuracion_es_requerida')),
    parent_vehicle_id: Yup.string().nullable()
  });

  const formik = useFormik({
    initialValues: {
      alias: '',
      make: '',
      model: '',
      type: '',
      year: new Date().getFullYear(),
      configuration_id: '',
      parent_vehicle_id: '',
      allow_dual_operator: false
    },
    validationSchema: vehicleSchema,
    onSubmit: (values) => {
      const submitData = {
        ...values,
        configuration_id: parseInt(values.configuration_id),
        parent_vehicle_id: values.parent_vehicle_id || null,
        allow_dual_operator: !!values.allow_dual_operator
      };

      createVehicle(submitData);
    },
  });

  const handleVehicleDialog = (vehicle = null) => {
    setVehicleDialog({ open: true, vehicle });
    if (vehicle) {
      formik.setValues({
        alias: vehicle.alias || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        type: vehicle.type || '',
        year: vehicle.year || new Date().getFullYear(),
        configuration_id: vehicle.configuration_id?.toString() || '',
        parent_vehicle_id: vehicle.parent_vehicle_id || '',
        allow_dual_operator: vehicle.allow_dual_operator ?? false
      });
    } else {
      formik.resetForm();
    }
  };

  const handleDelete = (vehicle) => {
    setDeleteDialog({ open: true, vehicle });
  };

  const confirmDelete = () => {
    deleteVehicle(deleteDialog.vehicle.id);
  };

  const filteredVehicles = allVehicles?.filter(vehicle => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      (vehicle.alias && vehicle.alias.toLowerCase().includes(searchLower)) ||
      (vehicle.make && vehicle.make.toLowerCase().includes(searchLower)) ||
      (vehicle.model && vehicle.model.toLowerCase().includes(searchLower)) ||
      (vehicle.type && vehicle.type.toLowerCase().includes(searchLower)) ||
      (vehicle.display_name && vehicle.display_name.toLowerCase().includes(searchLower))
    );

    if (!matchesSearch) return false;

    if (vehicleScopeFilter === 'parent') {
      return !vehicle.parent_vehicle_id;
    }
    if (vehicleScopeFilter === 'child') {
      return !!vehicle.parent_vehicle_id;
    }

    return true;
  }) || [];

  const getAvailableParentVehicles = (currentVehicle = null) => {
    if (!allVehicles) return [];

    return allVehicles.filter(vehicle => {
      if (currentVehicle && vehicle.id === currentVehicle.id) return false;
      if (vehicle.parent_vehicle_id) return false;
      if (currentVehicle && vehicle.parent_vehicle_id === currentVehicle.id) return false;
      return true;
    });
  };

  if (vehiclesLoading || configsLoading || sensorsLoading) {
    return <LoadingPage message={t('vehicles.loadingMessage')} />;
  }

  // Renderizar vista de cuadrícula OPTIMIZADA - MÁS COMPACTA Y RESPONSIVE
  const renderGridView = () => (
    <Grid container spacing={1.5}>
      {filteredVehicles.map((vehicle) => {
        const isChildVehicle = !!vehicle.parent_vehicle_id;

        return (
          <Grid
            item
            xs={6}
            sm={6}
            md={4}
            lg={3}
            xl={2.4}
            key={vehicle.id}
          >
            <Card
              sx={{
                height: 'fit-content',
                minHeight: 180,
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                ...(isChildVehicle && {
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: 'secondary.main',
                    opacity: 0.8
                  }
                }),
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[6],
                }
              }}
              onClick={() => setSelectedVehicle(vehicle)}
            >
              <CardContent sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Nombre centrado */}
                <Box sx={{ textAlign: 'center', mb: 0.25 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      lineHeight: 1.2
                    }}
                  >
                    {vehicle.display_name}
                  </Typography>
                </Box>

                {/* Chips centrados - MÁS COMPACTOS */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.4,
                    justifyContent: 'center',
                    minHeight: '36px'
                  }}
                >
                  {isChildVehicle && (
                    <Chip
                      label={t('vehicles.card.child')}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{
                        fontSize: '0.55rem',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.4, py: 0 }
                      }}
                    />
                  )}

                  {vehicle.make && vehicle.model && (
                    <Chip
                      label={`${vehicle.make} ${vehicle.model}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontSize: '0.6rem',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.4, py: 0 }
                      }}
                    />
                  )}

                  {vehicle.type && (
                    <Chip
                      label={vehicle.type.charAt(0).toUpperCase() + vehicle.type.slice(1)}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      sx={{
                        fontSize: '0.6rem',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.4, py: 0 }
                      }}
                    />
                  )}

                  {vehicle.year && (
                    <Chip
                      label={vehicle.year}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.6rem',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.4, py: 0 }
                      }}
                    />
                  )}
                </Box>

                {/* Esquemático de Sensores - AJUSTADO PARA MÓVIL */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 0.5,
                    minHeight: 70
                  }}
                >
                  <Box sx={{ transform: { xs: 'scale(1.3)', sm: 'scale(1.4)', md: 'scale(1.5)' } }}>
                    <SimpleSchematic vehicle={vehicle} t={t} />
                  </Box>
                </Box>

                {/* Vehículos Conectados - Solo para padres */}
                {vehicle.child_vehicles?.length > 0 && (
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem', mb: 0.4, display: 'block' }}
                    >
                      {t('vehicles.card.connected')} ({vehicle.child_vehicles.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
                      {vehicle.child_vehicles.slice(0, 2).map((child) => (
                        <Chip
                          key={child.id}
                          label={child.display_name}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{
                            fontSize: '0.55rem',
                            height: '16px',
                            '& .MuiChip-label': { px: 0.4, py: 0 }
                          }}
                        />
                      ))}
                      {vehicle.child_vehicles.length > 2 && (
                        <Chip
                          label={`+${vehicle.child_vehicles.length - 2}`}
                          size="small"
                          variant="outlined"
                          color="default"
                          sx={{
                            fontSize: '0.55rem',
                            height: '16px',
                            '& .MuiChip-label': { px: 0.4, py: 0 }
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {/* Parent vehicle info para hijos */}
                {isChildVehicle && (
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem', mb: 0.4, display: 'block' }}
                    >
                      {t('vehicles.card.connectedTo')}:
                    </Typography>
                    <Chip
                      label={vehicles?.find(v => v.id === vehicle.parent_vehicle_id)?.display_name || t('hardcoded.pages_vehiclespage.vehiculo_padre')}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{
                        fontSize: '0.55rem',
                        height: '16px',
                        '& .MuiChip-label': { px: 0.4, py: 0 }
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      {filteredVehicles.length === 0 && !vehiclesLoading && (
        <Grid item xs={12}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <VehicleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('vehicles.noVehiclesFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? t('vehicles.adjustCriteria') : t('vehicles.addFirst')}
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  // Renderizar vista de tabla
  const renderTableView = () => (
    <AdvancedDataTable
      storageKey="vehicles_table"
      rows={filteredVehicles}
      rowKey="id"
      initialSortBy="display_name"
      columns={[
        {
          id: 'display_name',
          label: t('vehicles.table.vehicle'),
          accessor: (row) => row.display_name,
          filterable: true,
          render: (row) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VehicleIcon color="primary" />
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>{row.display_name}</Typography>
                {!!row.parent_vehicle_id && <Chip label={t('vehicles.card.child')} size="small" color="secondary" variant="outlined" />}
              </Box>
            </Box>
          )
        },
        {
          id: 'make_model',
          label: t('vehicles.table.makeModel'),
          accessor: (row) => `${row.make || ''} ${row.model || ''}`.trim(),
          filterable: true,
          render: (row) => (row.make || row.model) ? `${row.make || ''} ${row.model || ''}`.trim() : t('vehicles.table.notSpecified')
        },
        {
          id: 'type',
          label: t('vehicles.table.type'),
          accessor: (row) => row.type || '',
          filterable: true,
          render: (row) => row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : t('vehicles.table.notSpecified')
        },
        {
          id: 'receiver_count',
          label: 'Receptores',
          accessor: (row) => row.receiver_count || 0,
          filterable: true
        },
        {
          id: 'sensor_count',
          label: 'Sensores',
          accessor: (row) => row.sensor_count || 0,
          filterable: true
        }
      ]}
      onRowClick={(row) => setSelectedVehicle(row)}
      emptyTitle={t('vehicles.noVehiclesFound')}
      emptyDescription={searchTerm ? t('vehicles.adjustCriteria') : t('vehicles.addFirst')}
    />
  );

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('vehicles.title')}
        </Typography>
      </Box>

      <AIRecommendationsPanel module="vehicles" />

      {/* Controles */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('vehicles.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Tooltip title={t('common.refresh')}>
            <IconButton
              onClick={refetchVehicles}
              size="small"
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {/* Alternar Vista */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, newViewMode) => {
              if (newViewMode !== null) {
                setViewMode(newViewMode);
              }
            }}
            size="small"
          >
            <ToggleButton value="grid" aria-label={t('vehicles.viewModes.grid')}>
              <Tooltip title={t('vehicles.viewModes.grid')}>
                <GridViewIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label={t('vehicles.viewModes.table')}>
              <Tooltip title={t('vehicles.viewModes.table')}>
                <TableViewIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {hasPermission('manager') && (
          <Button
            data-tutorial-id="add-vehicle-btn"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleVehicleDialog()}
            size="large"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('vehicles.addVehicle')}
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setVehicleScopeFilter('all')}>
              <Typography variant="h4" fontWeight={700} color="primary.main">{allVehicles.length}</Typography>
              <Typography variant="body2" color="text.secondary">{t('common.total')}</Typography>
              {vehicleScopeFilter === 'all' && <Chip size="small" color="primary" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setVehicleScopeFilter('parent')}>
              <Typography variant="h4" fontWeight={700} color="success.main">{allVehicles.filter((v) => !v.parent_vehicle_id).length}</Typography>
              <Typography variant="body2" color="text.secondary">{t('pages_vehiclespage.parent_label')}</Typography>
              {vehicleScopeFilter === 'parent' && <Chip size="small" color="success" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setVehicleScopeFilter('child')}>
              <Typography variant="h4" fontWeight={700} color="secondary.main">{allVehicles.filter((v) => !!v.parent_vehicle_id).length}</Typography>
              <Typography variant="body2" color="text.secondary">{t('pages_vehiclespage.children_label')}</Typography>
              {vehicleScopeFilter === 'child' && <Chip size="small" color="secondary" label={t('common.active')} sx={{ mt: 1 }} />}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contenido de Vehículos */}
      {viewMode === 'grid' ? renderGridView() : renderTableView()}

      {/* Diálogo de Vehículo - SOLO CREAR */}
      <Dialog
        open={vehicleDialog.open}
        onClose={() => setVehicleDialog({ open: false, vehicle: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          'data-tutorial-id': 'vehicle-form-dialog',
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {t('vehicles.form.createTitle')}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('vehicles.form.alias')}
                  name="alias"
                  value={formik.values.alias}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.alias && Boolean(formik.errors.alias)}
                  helperText={formik.touched.alias && formik.errors.alias}
                  placeholder={t('vehicles.form.aliasHelper')}
                  inputProps={{
                    'data-tutorial-id': 'vehicle-form-alias',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('vehicles.form.make')}
                  name="make"
                  value={formik.values.make}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.make && Boolean(formik.errors.make)}
                  helperText={formik.touched.make && formik.errors.make}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('vehicles.form.model')}
                  name="model"
                  value={formik.values.model}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.model && Boolean(formik.errors.model)}
                  helperText={formik.touched.model && formik.errors.model}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('vehicles.form.type')}
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                  SelectProps={{
                    inputProps: {
                      'data-tutorial-id': 'vehicle-form-type',
                    },
                  }}
                >
                  <MenuItem value="">{t('vehicles.form.selectType')}</MenuItem>
                  {VEHICLE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('vehicles.form.year')}
                  name="year"
                  value={formik.values.year}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.year && Boolean(formik.errors.year)}
                  helperText={formik.touched.year && formik.errors.year}
                  inputProps={{
                    'data-tutorial-id': 'vehicle-form-year',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('vehicles.form.configuration')}
                  name="configuration_id"
                  value={formik.values.configuration_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.configuration_id && Boolean(formik.errors.configuration_id)}
                  helperText={formik.touched.configuration_id && formik.errors.configuration_id}
                >
                  <MenuItem value="">{t('vehicles.form.selectConfiguration')}</MenuItem>
                  {vehicleConfigs?.map((config) => (
                    <MenuItem key={config.configurationID} value={config.configurationID}>
                      {config.configurationP} ({config.positions} {t('vehicles.table.positions')})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label={t('vehicles.form.parentVehicle')}
                  name="parent_vehicle_id"
                  value={formik.values.parent_vehicle_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.parent_vehicle_id && Boolean(formik.errors.parent_vehicle_id)}
                  helperText={formik.touched.parent_vehicle_id && formik.errors.parent_vehicle_id}
                >
                  <MenuItem value="">{t('vehicles.form.noParentVehicle')}</MenuItem>
                  {getAvailableParentVehicles(vehicleDialog.vehicle).map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.display_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* ¿Se conduce la unidad con doble operador? */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {t('hardcoded.components_vehicles_vehicleform.se_conduce_la_unidad_con')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('hardcoded.components_vehicles_detail_vehicleoverviewtab.no_maximo_1_operador_si')}</Typography>
                  </Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={formik.values.allow_dual_operator ? 'yes' : t('common.no')}
                    onChange={(e, val) => {
                      if (val !== null) formik.setFieldValue('allow_dual_operator', val === 'yes');
                    }}
                  >
                    <ToggleButton value={t('common.no')}>{t('common.no')}</ToggleButton>
                    <ToggleButton value="yes">Sí</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVehicleDialog({ open: false, vehicle: null })}>
              {t('vehicles.form.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={creating}
            >
              {t('vehicles.form.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, vehicle: null })}
      >
        <DialogTitle>
          {t('vehicles.actions.confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('vehicles.actions.deleteMessage')}
          </Typography>
          {deleteDialog.vehicle && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {deleteDialog.vehicle.display_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {deleteDialog.vehicle.make} {deleteDialog.vehicle.model} {deleteDialog.vehicle.year && `(${deleteDialog.vehicle.year})`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, vehicle: null })}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalle del Vehículo - CON EDICIÓN Y ASIGNACIÓN */}
      <VehicleDetail
        open={!!selectedVehicle}
        onClose={() => setSelectedVehicle(null)}
        vehicle={selectedVehicle}
        vehicleId={selectedVehicle?.id}
        onSensorUnassigned={() => refetchVehicles()}
        onVehicleUpdated={() => refetchVehicles()}
        onVehicleDeleted={() => {
          setSelectedVehicle(null);
          refetchVehicles();
        }}
        allVehicles={allVehicles}
        sensors={sensors}
        vehicleConfigs={vehicleConfigs}
      />
    </Container>
  );
};

export default VehiclesPage;